import { isSimpleMode } from './simpleMode'

export type FieldKind = 'text' | 'date' | 'decimal' | 'integer' | 'select' | 'multiselect' | 'boolean'
export type FieldPermission = string | readonly string[]

export interface FieldDef {
  key: string
  label: string
  labelEn?: string
  kind?: FieldKind
  source?: string
  required?: boolean
  defaultValue?: string | number | boolean
  permission?: FieldPermission
  editPermission?: FieldPermission
  immutableAfterCreate?: boolean
  filterKey?: string
  defaultFrozen?: boolean
  numeric?: { precision?: number; maxDigits?: number; min?: string; max?: string }
}

export interface ActionDef {
  key: string
  label: string
  labelEn?: string
  statuses?: string[]
  invoiceStatuses?: string[]
  permission?: string
  fields?: FieldDef[]
  lineFields?: FieldDef[]
  collection?: boolean
  collectionPath?: string
  collectionIdField?: string
  /** Restricted partner commands load a fresh version and only writable financial fields. */
  financialMaintenance?: boolean
}

export interface EntityConfig {
  key: string
  group: string
  title: string
  titleEn: string
  path: string
  permission: string
  createPermission?: string
  editPermission?: string
  /** Occupancy status is not an approval lifecycle; domain validation stays server-side. */
  editStatePolicy?: 'master-data'
  navigation?: boolean
  /** Server-side export permission. Without it the export request is rejected with 403. */
  exportPermission?: string
  importPermission?: string
  columns: FieldDef[]
  fields?: FieldDef[]
  lineCollections?: { key: string; label: string; labelEn?: string; fields: FieldDef[] }[]
  actions?: ActionDef[]
}

const FIELD_LABEL_EN: Record<string, string> = {
  shipping_address: 'Shipping address', address_snapshot: 'Approved shipping address',
  username: 'Username',
  product: 'Product', quantity: 'Quantity', unit: 'Unit', sku: 'SKU', name_zh: 'Chinese name', name_en: 'English name',
  base_unit_code: 'Inventory unit', sale_unit_code: 'Sales unit', reference_price: 'Reference price', reference_cost: 'Reference cost',
  category: 'Category', base_unit: 'Inventory unit', sale_unit: 'Sales unit', default_temp_zone: 'Temperature zone',
  shelf_life_days: 'Shelf life (days)', tax_rate: 'Tax rate', batch_managed: 'Batch managed', conversion_rate: 'KG per unit',
  use_inbound: 'Inbound enabled', use_outbound: 'Sales enabled', is_default_in: 'Default inbound', is_default_out: 'Default sales',
  customer_level: 'Customer level', price: 'Price', cost: 'Cost', code: 'Code', level_code: 'Level', settlement: 'Settlement terms',
  credit_limit: 'Credit limit', level_name: 'Customer grade', level: 'Level', contact: 'Contact', payment_days: 'Payment days', address: 'Address',
  temp_requirement: 'Temperature requirement', temp_zone: 'Temperature zone', capacity_m3: 'Capacity m³',
  load_limit_kg: 'Load limit KG', enable_location: 'Enable locations', warehouse_code: 'Warehouse',
  warehouse_name: 'Warehouse', from_warehouse_name: 'Source warehouse', to_warehouse_name: 'Destination warehouse',
  zone_name: 'Zone', zone_code: 'Zone code', location_code: 'Bin', location_name: 'Bin name',
  pallet: 'Pallet', pallet_code: 'Pallet code', pallet_name: 'Pallet name',
  parent: 'Parent',
  warehouse: 'Warehouse', pallet_slots: 'Pallet slots', parent_name: 'Parent category', expiry_warning_ratio: 'Warning ratio',
  expiry_critical_ratio: 'Critical ratio', sort: 'Sort order', freight_by: 'Freight paid by', insurance_by: 'Insurance paid by',
  duty_by: 'Duty paid by', requester_name: 'Requester', request_date: 'Request date', status: 'Status', department: 'Department',
  need_date: 'Required date', purpose: 'Purpose', doc_no: 'Document no.', supplier_name: 'Supplier', order_date: 'Order date',
  request: 'Purchase request', supplier: 'Supplier', delivery_date: 'Delivery date', warehouse_id: 'Warehouse',
  receipt_date: 'Receipt date', order_line_id: 'Order line ID', batch_no: 'Batch no.', production_date: 'Production date',
  expiry_date: 'Expiry date', order: 'Order ID', customer_name: 'Customer', customer: 'Customer', incoterm: 'Incoterm',
  duty_rate: 'Duty rate', outbound_date: 'Outbound date', cost_amount: 'Outbound cost', product_sku: 'SKU',
  available_kg: 'Available KG', reserved_kg: 'Reserved KG', cost_per_kg: 'Cost / KG', occurred_on: 'Business date', movement_type: 'Movement type',
  quantity_kg: 'Quantity KG', amount: 'Amount', source_no: 'Source document', take_date: 'Count date', take_type: 'Count type',
  freeze: 'Freeze outbound', batch: 'Batch', actual_quantity_kg: 'Counted KG', from_warehouse: 'From warehouse',
  to_warehouse: 'To warehouse', transfer_date: 'Transfer date', source_batch: 'Source batch', scrap_date: 'Scrap date',
  reason: 'Reason', loss_amount: 'Loss amount', evidence: 'Evidence', invoice_no: 'Receivable no.', due_date: 'Due date',
  received_amount: 'Received', unreceived_amount: 'Outstanding', customer_id: 'Customer', method: 'Payment method',
  receivable_id: 'Receivable', receipt_no: 'Receipt no.', allocated_amount: 'Allocated', bill_no: 'Payable no.',
  held_amount: 'Order advances held', available_amount: 'Unallocated and unreserved',
  gross_amount: 'Payable', paid_amount: 'Paid', unpaid_amount: 'Outstanding', invoice_status: 'Invoice status', invoice_date: 'Invoice date',
  invoice_amount: 'Invoice amount incl. tax', supplier_id: 'Supplier', payment_date: 'Payment date', reference_no: 'Bank reference',
  payable_id: 'Payable', payment_no: 'Payment no.', created_at: 'Time', actor_name: 'Operator', action: 'Action',
  object_type: 'Object', object_no: 'Document no.', ip: 'IP',
  unit_type: 'Unit type', decimal_places: 'Decimal places', is_active: 'Active', remark: 'Remarks',
  specification_zh: 'Chinese specification', measurement_mode: 'Measurement mode',
  owner: 'Owner', owner_name: 'Owner', contact_name: 'Contact name', phone: 'Phone', email: 'Email',
  settlement_method: 'Legacy mixed settlement (read-only)', settlement_terms_type: 'Settlement terms',
  default_payment_method: 'Default receipt/payment method', default_language: 'Document language',
  bank_account: 'Bank account', purchase_lead_days: 'Purchase lead days',
  taxpayer_no: 'Taxpayer number', manager: 'Warehouse manager',
  manager_name: 'Warehouse manager', direct_product_count: 'Direct products', total_product_count: 'Products incl. subcategories',
  address_code: 'Address code', receiver_name: 'Receiver', receiver_phone: 'Receiver phone',
  province: 'Province', city: 'City', district: 'District', detail_address: 'Address detail',
  temp_zones: 'Accepted temperature zones', delivery_window: 'Delivery window', is_default: 'Default address',
  allow_mixed_sku: 'Mixed SKUs', allow_mixed_batch: 'Mixed batches', pick_sequence: 'Pick sequence',
  capacity_warning_ratio: 'Capacity warning ratio', load_warning_ratio: 'Load warning ratio',
}

const fieldLabelEn = (key: string) => FIELD_LABEL_EN[key]
const text = (key: string, label: string, required = false): FieldDef => ({ key, label, labelEn: fieldLabelEn(key), kind: 'text', required })
const date = (key: string, label: string, required = false): FieldDef => ({ key, label, labelEn: fieldLabelEn(key), kind: 'date', required })
const decimal = (key: string, label: string, required = false): FieldDef => ({ key, label, labelEn: fieldLabelEn(key), kind: 'decimal', required })
const integer = (key: string, label: string, required = false): FieldDef => ({ key, label, labelEn: fieldLabelEn(key), kind: 'integer', required })
const select = (key: string, label: string, source: string, required = false): FieldDef => ({
  key, label, labelEn: fieldLabelEn(key), kind: 'select', source, required,
})
const bool = (key: string, label: string, defaultValue = false): FieldDef => ({
  key, label, labelEn: fieldLabelEn(key), kind: 'boolean', defaultValue,
})
const filterable = (field: FieldDef, filterKey: string, defaultFrozen = false): FieldDef => ({
  ...field, filterKey, defaultFrozen,
})

const productLine = [select('product', '商品', 'products', true), decimal('quantity', '数量', true),
  select('unit', '单位', 'units', true)]
const orderActions: ActionDef[] = [
  { key: 'submit', label: '提交审核', labelEn: 'Submit for approval', statuses: ['draft'] },
  { key: 'close', label: '关闭', labelEn: 'Close', statuses: ['approved', 'partial'] },
  { key: 'void', label: '作废', labelEn: 'Void', statuses: ['draft'] },
]

export const entities: Record<string, EntityConfig> = {
  products: {
    key: 'products', group: '基础档案', title: '商品档案', titleEn: 'Products',
    path: '/api/v1/master-data/products', permission: 'masterdata.view_product',
    createPermission: 'masterdata.add_product', editPermission: 'masterdata.change_product',
    exportPermission: 'masterdata.view_product_export', importPermission: 'masterdata.import_product',
    columns: [filterable(text('sku', 'SKU'), 'f_sku', true),
      filterable(text('name_zh', '商品名称'), 'f_name_zh'), text('base_unit_code', '库存单位'),
      text('sale_unit_code', '销售单位'), text('specification_zh', '中文品规'),
      text('measurement_mode', '计量模式'), bool('is_active', '启用')],
    fields: [text('sku', 'SKU', true), text('name_zh', '中文名', true), text('name_en', '英文名', true),
      select('category', '分类', 'product_categories'), select('base_unit', '库存单位', 'units', true),
      select('sale_unit', '销售单位', 'units', true), select('default_temp_zone', '温区', 'temp_zones', true),
      integer('shelf_life_days', '保质期天数'), decimal('tax_rate', '税率'), bool('batch_managed', '批次管理', true)],
    lineCollections: [
      { key: 'units', label: '单位换算', labelEn: 'Unit conversions', fields: [select('unit', '单位', 'units', true),
        decimal('conversion_rate', '1单位折合KG', true), bool('use_inbound', '可入库', true),
        bool('use_outbound', '可销售', true), bool('is_default_in', '默认入库'), bool('is_default_out', '默认销售')] },
    ],
  },
  units: {
    key: 'units', group: '基础档案', title: '计量单位', titleEn: 'Units',
    path: '/api/v1/master-data/units', permission: 'masterdata.view_unit',
    createPermission: 'masterdata.add_unit', editPermission: 'masterdata.change_unit',
    exportPermission: 'masterdata.view_unit_export', importPermission: 'masterdata.import_unit',
    columns: [filterable(text('code', '单位编码'), 'f_code', true),
      filterable(text('name_zh', '中文名'), 'f_name_zh'), text('name_en', '英文名'),
      text('unit_type', '单位类型'), decimal('decimal_places', '小数位'), bool('is_active', '启用')],
    fields: [{ ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true),
      { ...select('unit_type', '单位类型', 'unit_types', true), defaultValue: 'package' },
      { ...select('decimal_places', '小数位', 'decimal_places', true), defaultValue: '0' },
      bool('is_active', '启用'), text('remark', '备注')],
  },
  customers: {
    key: 'customers', group: '基础档案', title: '客户档案', titleEn: 'Customers',
    path: '/api/v1/master-data/customers', permission: 'masterdata.view_customer',
    createPermission: 'masterdata.add_customer', editPermission: 'masterdata.change_customer',
    exportPermission: 'masterdata.view_customer_export', importPermission: 'masterdata.import_customer',
    columns: [filterable(text('code', '客户编码'), 'f_code', true),
      filterable(text('name_zh', '中文名'), 'f_name_zh'), text('level_name', '客户等级'), text('owner_name', '负责人'),
      bool('is_active', '启用'),
      { ...decimal('credit_limit', '授信额度'), permission: 'masterdata.view_customer_credit' }],
    fields: [{ ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true),
      select('level', '客户等级', 'customer_levels'),
      { ...text('contact_name', '联系人'), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('phone', '联系电话'), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('email', '邮箱'), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('settlement_method', '旧混合结算值（只读）'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      { ...select('settlement_terms_type', '结算条款', 'settlement_terms_types'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      { ...select('default_payment_method', '默认收付款方式', 'default_payment_methods'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      { ...integer('payment_days', '账期天数'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      { ...decimal('tax_rate', '默认税率%'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      { ...decimal('credit_limit', '授信额度'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_credit' },
      { ...text('credit_change_reason', '授信变更原因（变更时必填）'), labelEn: 'Credit change reason (required for changes)',
        permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_credit' },
      { ...text('taxpayer_no', '税号'), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      select('default_language', '对外单据语言', 'document_languages'), bool('is_active', '启用'), text('remark', '备注')],
    actions: [{ key: 'finance', label: '维护结算资料', labelEn: 'Maintain settlement details',
      permission: 'masterdata.change_customer_finance', financialMaintenance: true, fields: [
        { ...select('settlement_terms_type', '结算条款', 'settlement_terms_types'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
        { ...select('default_payment_method', '默认收付款方式', 'default_payment_methods'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
        { ...integer('payment_days', '账期天数', true), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
        { ...decimal('tax_rate', '默认税率%'), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_finance' },
      ] }, { key: 'credit', label: '调整授信额度', labelEn: 'Adjust credit limit',
      permission: 'masterdata.change_customer_credit', financialMaintenance: true, fields: [
        { ...decimal('credit_limit', '授信额度', true), permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_credit' },
        { ...text('credit_change_reason', '授信变更原因（变更时必填）'), labelEn: 'Credit change reason (required for changes)', permission: 'masterdata.view_customer_credit', editPermission: 'masterdata.change_customer_credit' },
      ] }, { key: 'transfer', label: '移交负责人', labelEn: 'Transfer owner',
      permission: 'masterdata.transfer_customer', fields: [
        select('owner', '新负责人', 'customer_assignees', true), text('reason', '移交原因', true),
      ] }],
  },
  customer_levels: {
    key: 'customer_levels', group: '基础档案', title: '客户等级', titleEn: 'Customer levels', navigation: false,
    path: '/api/v1/master-data/customer-levels', permission: 'masterdata.view_customerlevel',
    createPermission: 'masterdata.add_customerlevel', editPermission: 'masterdata.change_customerlevel',
    exportPermission: 'masterdata.view_customerlevel_export', importPermission: 'masterdata.import_customerlevel',
    columns: [text('code', '等级编码'), text('name_zh', '中文名'), text('name_en', '英文名'),
      integer('sort', '排序'), bool('is_active', '启用')],
    fields: [{ ...text('code', '等级编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true), integer('sort', '排序'),
      bool('is_active', '启用'), text('remark', '备注')],
  },
  customer_addresses: {
    key: 'customer_addresses', group: '基础档案', title: '客户收货地址', titleEn: 'Customer addresses',
    navigation: false,
    path: '/api/v1/master-data/customer-addresses', permission: 'masterdata.view_customeraddress',
    createPermission: 'masterdata.add_customeraddress', editPermission: 'masterdata.change_customeraddress',
    exportPermission: 'masterdata.view_customeraddress_export', importPermission: 'masterdata.import_customeraddress',
    columns: [text('address_code', '地址编码'),
      { ...text('receiver_name', '收货人'), permission: 'masterdata.view_customer_contact' },
      { ...text('city', '城市'), permission: 'masterdata.view_customer_contact' },
      bool('is_default', '默认地址'), bool('is_active', '启用')],
    fields: [{ ...select('customer', '客户', 'customers', true), immutableAfterCreate: true },
      { ...text('address_code', '地址编码', true), immutableAfterCreate: true },
      { ...text('receiver_name', '收货人', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('receiver_phone', '收货电话', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('province', '省份', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('city', '城市', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('district', '区县', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { ...text('detail_address', '详细地址', true), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      { key: 'temp_zones', label: '可接收温区', labelEn: 'Accepted temperature zones', kind: 'multiselect',
        source: 'temp_zones', required: true },
      { ...text('delivery_window', '配送时间窗'), permission: 'masterdata.view_customer_contact', editPermission: 'masterdata.change_customer_contact' },
      bool('is_default', '默认地址'), bool('is_active', '启用', true), text('remark', '备注')],
    actions: [{ key: 'set-default', label: '设为默认地址', labelEn: 'Set default address',
      permission: 'masterdata.change_customeraddress' }],
  },
  suppliers: {
    key: 'suppliers', group: '基础档案', title: '供应商档案', titleEn: 'Suppliers',
    path: '/api/v1/master-data/suppliers', permission: 'masterdata.view_supplier',
    createPermission: 'masterdata.add_supplier', editPermission: 'masterdata.change_supplier',
    exportPermission: 'masterdata.view_supplier_export', importPermission: 'masterdata.import_supplier',
    columns: [filterable(text('code', '编码'), 'f_code', true),
      filterable(text('name_zh', '中文名'), 'f_name_zh'), text('owner_name', '负责人'), bool('is_active', '启用')],
    fields: [{ ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true),
      { ...text('contact_name', '联系人'), permission: 'masterdata.view_supplier_contact', editPermission: 'masterdata.change_supplier_contact' },
      { ...text('phone', '联系电话'), permission: 'masterdata.view_supplier_contact', editPermission: 'masterdata.change_supplier_contact' },
      { ...text('email', '邮箱'), permission: 'masterdata.view_supplier_contact', editPermission: 'masterdata.change_supplier_contact' },
      { ...text('settlement_method', '旧混合结算值（只读）'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      { ...select('settlement_terms_type', '结算条款', 'settlement_terms_types'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      { ...select('default_payment_method', '默认收付款方式', 'default_payment_methods'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      { ...integer('payment_days', '账期天数'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      { ...decimal('tax_rate', '默认税率%'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      { ...text('bank_account', '银行账号'), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
      integer('purchase_lead_days', '采购提前期'),
      select('default_language', '对外单据语言', 'document_languages'), bool('is_active', '启用'), text('remark', '备注')],
    actions: [{ key: 'terms', label: '维护结算条款', labelEn: 'Maintain settlement terms',
      permission: 'masterdata.change_supplier_terms', financialMaintenance: true, fields: [
        { ...select('settlement_terms_type', '结算条款', 'settlement_terms_types'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
        { ...select('default_payment_method', '默认收付款方式', 'default_payment_methods'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
        { ...integer('payment_days', '账期天数', true), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
        { ...decimal('tax_rate', '默认税率%'), permission: ['masterdata.view_supplier_terms', 'masterdata.view_supplier_finance'], editPermission: ['masterdata.change_supplier_terms', 'masterdata.change_supplier_finance'] },
      ] }, { key: 'finance', label: '维护结算资料', labelEn: 'Maintain settlement details',
      permission: 'masterdata.change_supplier_finance', financialMaintenance: true, fields: [
        { ...select('settlement_terms_type', '结算条款', 'settlement_terms_types'), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
        { ...select('default_payment_method', '默认收付款方式', 'default_payment_methods'), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
        { ...integer('payment_days', '账期天数', true), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
        { ...decimal('tax_rate', '默认税率%'), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
        { ...text('bank_account', '银行账号'), permission: 'masterdata.view_supplier_finance', editPermission: 'masterdata.change_supplier_finance' },
      ] }, { key: 'transfer', label: '移交负责人', labelEn: 'Transfer owner',
      permission: 'masterdata.transfer_supplier', fields: [
        select('owner', '新负责人', 'supplier_assignees', true), text('reason', '移交原因', true),
      ] }],
  },
  warehouses: {
    key: 'warehouses', group: '基础档案', title: '仓库', titleEn: 'Warehouses',
    path: '/api/v1/master-data/warehouses', permission: 'masterdata.view_warehouse',
    createPermission: 'masterdata.add_warehouse', editPermission: 'masterdata.change_warehouse',
    exportPermission: 'masterdata.view_warehouse_export', importPermission: 'masterdata.import_warehouse',
    columns: [filterable(text('code', '仓库编码'), 'f_code', true),
      filterable(text('name_zh', '中文名'), 'f_name_zh'),
      filterable(select('temp_zone', '温区', 'temp_zones'), 'f_temp_zone'),
      decimal('capacity_m3', '容量m³'), decimal('load_limit_kg', '承重KG'), text('manager_name', '负责人'), bool('is_active', '启用')],
    fields: [{ ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true),
      select('temp_zone', '温区', 'temp_zones', true), decimal('capacity_m3', '容量m³', true), decimal('load_limit_kg', '承重KG', true),
      integer('pallet_slots', '托盘位（可空）'), text('address', '仓库地址'),
      select('manager', '仓库负责人', 'warehouse_managers'),
      bool('enable_location', '启用货位'), bool('is_active', '启用'), text('remark', '备注')],
  },
  locations: {
    key: 'locations', group: '基础档案', title: '货位', titleEn: 'Locations',
    path: '/api/v1/master-data/locations', permission: 'masterdata.view_location',
    createPermission: 'masterdata.add_location', editPermission: 'masterdata.change_location',
    editStatePolicy: 'master-data',
    exportPermission: 'masterdata.view_location_export', importPermission: 'masterdata.import_location',
    columns: [{ ...text('warehouse_name', '仓库'), labelEn: 'Warehouse' },
      { ...text('parent_name', '所属库区'), labelEn: 'Zone' },
      filterable(text('code', '货位编码'), 'f_code', true),
      filterable(text('name_zh', '名称'), 'f_name_zh'),
      decimal('level', '层级'), text('temp_zone', '温区')],
    fields: [{ ...select('warehouse', '仓库', 'location_warehouses', true), immutableAfterCreate: true },
      { ...select('parent', '所属库区', 'location_parents'), immutableAfterCreate: true },
      text('code', '编码', true), text('name_zh', '中文名', true),
      text('name_en', '英文名', true), decimal('capacity_m3', '容量m³', true), decimal('load_limit_kg', '承重KG', true),
      integer('pallet_slots', '托盘位（可空）'),
      decimal('capacity_warning_ratio', '空间预警比例 0–1'), decimal('load_warning_ratio', '承重预警比例 0–1'),
      bool('allow_mixed_sku', '允许混SKU'), bool('allow_mixed_batch', '允许混批次'),
      integer('pick_sequence', '拣货顺序'), bool('is_active', '启用', true), text('remark', '备注')],
  },
  pallets: {
    key: 'pallets', group: '基础档案', title: '托盘', titleEn: 'Pallets',
    path: '/api/v1/master-data/pallets', permission: 'masterdata.view_pallet',
    createPermission: 'masterdata.add_pallet', editPermission: 'masterdata.change_pallet',
    editStatePolicy: 'master-data',
    exportPermission: 'masterdata.view_pallet_export',
    columns: [{ ...text('warehouse_name', '仓库'), labelEn: 'Warehouse' },
      { ...text('zone_name', '库区'), labelEn: 'Zone' },
      { ...text('location_code', '货位'), labelEn: 'Bin' },
      filterable(text('code', '托盘编码'), 'f_code', true),
      filterable(text('name_zh', '名称'), 'f_name_zh'),
      bool('is_active', '启用')],
    fields: [{ ...select('warehouse', '仓库', 'location_warehouses', true), immutableAfterCreate: true },
      { ...select('location', '货位', 'physical_locations', true), labelEn: 'Bin', immutableAfterCreate: true },
      { ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '名称'), text('name_en', '英文名'),
      bool('is_active', '启用', true), text('remark', '备注')],
  },
  categories: {
    key: 'categories', group: '基础档案', title: '商品分类', titleEn: 'Categories',
    path: '/api/v1/master-data/product-categories', permission: 'masterdata.view_productcategory',
    createPermission: 'masterdata.add_productcategory', editPermission: 'masterdata.change_productcategory',
    exportPermission: 'masterdata.view_productcategory_export', importPermission: 'masterdata.import_productcategory',
    columns: [filterable(text('code', '分类编码'), 'f_code', true),
      filterable(text('name_zh', '中文名'), 'f_name_zh'), text('name_en', '英文名'),
      text('parent_name', '上级分类'), decimal('level', '层级'), bool('is_active', '启用'),
      { ...integer('direct_product_count', '直接商品数'), permission: 'masterdata.view_product' },
      { ...integer('total_product_count', '汇总商品数（含子类）'), permission: 'masterdata.view_product' },
      decimal('expiry_warning_ratio', '普通预警比例'), decimal('expiry_critical_ratio', '严重预警比例')],
    fields: [{ ...text('code', '编码', true), immutableAfterCreate: true },
      text('name_zh', '中文名', true), text('name_en', '英文名', true),
      select('parent', '上级分类', 'category_parents'), { ...integer('sort', '排序'), defaultValue: 10 },
      { ...decimal('expiry_warning_ratio', '普通预警比例 0–1'), defaultValue: '0.3000' },
      { ...decimal('expiry_critical_ratio', '严重预警比例 0–1'), defaultValue: '0.1500' },
      bool('is_active', '启用'), text('remark', '备注')],
  },
  purchase_requests: {
    key: 'purchase_requests', group: '采购', title: '采购申请', titleEn: 'Purchase Requests',
    path: '/api/v1/procurement/requests', permission: 'procurement.view_purchaserequest',
    createPermission: 'procurement.add_purchaserequest', editPermission: 'procurement.change_purchaserequest',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true), text('requester_name', '申请人'),
      date('request_date', '申请日'), text('owner_name', '当前负责人'),
      { ...text('owner_department', '负责部门'), labelEn: 'Responsible department' },
      text('status', '状态'), { key: 'conversion_state', label: '申请转单状态', labelEn: 'Request conversion state' }],
    fields: [text('requester_name', '申请人', true), text('department', '部门'),
      date('request_date', '申请日', true), date('need_date', '需求日'), select('warehouse', '仓库', 'warehouses', true)],
    lineCollections: [{ key: 'lines', label: '申请明细', labelEn: 'Request lines', fields: [...productLine, date('need_date', '需求日'), text('purpose', '用途')] }],
    actions: [...orderActions.map((item) => item.key === 'close'
      ? { ...item, statuses: ['approved', 'partial', 'executed'] } : item),
    { key: 'transfer', label: '移交负责人', labelEn: 'Transfer responsibility',
      statuses: ['draft', 'pending', 'approved', 'partial'], permission: 'procurement.transfer_purchaserequest',
      fields: [select('owner', '新负责人', 'purchase_request_assignees', true), text('reason', '移交原因', true)] }],
  },
  purchase_orders: {
    key: 'purchase_orders', group: '采购', title: '采购订单', titleEn: 'Purchase Orders',
    path: '/api/v1/procurement/orders', permission: 'procurement.view_purchaseorder',
    createPermission: 'procurement.add_purchaseorder', editPermission: 'procurement.change_purchaseorder',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      filterable(text('supplier_name', '供应商'), 'f_supplier_name'), date('order_date', '订单日'),
      text('status', '状态'), text('owner_name', '当前负责人'),
      { ...text('owner_department', '负责部门'), labelEn: 'Responsible department' }],
    fields: [select('request', '采购申请', 'purchase_requests'), select('supplier', '供应商', 'suppliers', true),
      select('warehouse', '目标仓库', 'warehouses', true),
      date('order_date', '订单日', true), date('delivery_date', '交期')],
    lineCollections: [{ key: 'lines', label: '订单明细', labelEn: 'Order lines', fields: [
      { key: 'requested_line', label: '来源申请明细', labelEn: 'Source request line', kind: 'select' },
      select('product', '商品', 'products', true),
      { ...decimal('quantity', '履约数量', true), labelEn: 'Fulfillment quantity', numeric: { precision: 3, min: '0.001' } },
      { ...select('unit', '履约单位', 'units', true), labelEn: 'Fulfillment unit' },
      { ...select('pricing_unit', '计价单位', 'units', true), labelEn: 'Pricing unit' },
      { ...decimal('price', '计价单价', true), labelEn: 'Price per pricing unit',
        permission: 'procurement.view_purchase_amount' },
      { ...decimal('tax_rate', '税率 %（空取档案默认）'), labelEn: 'Tax % (blank uses master default)',
        numeric: { precision: 4, min: '0', max: '100' }, permission: 'procurement.view_purchase_amount' }] }],
    actions: [...orderActions,
      { key: 'transfer', label: '移交负责人', labelEn: 'Transfer responsibility',
        statuses: ['draft', 'pending', 'approved', 'partial'], permission: 'procurement.transfer_purchaseorder',
        fields: [select('owner', '新负责人', 'purchase_order_assignees', true), text('reason', '移交原因', true)] },
      { key: 'receive', label: '采购入库', labelEn: 'Receive purchase', statuses: ['approved', 'partial'],
      permission: 'procurement.add_purchasereceipt',
      fields: [select('warehouse_id', '入库仓', 'warehouses', true), date('receipt_date', '入库日', true)],
      lineFields: [decimal('order_line_id', '订单行ID', true), decimal('quantity', '入库数量', true),
        text('batch_no', '批次号', true), date('production_date', '生产日'), date('expiry_date', '到期日', true)] }],
  },
  purchase_receipts: {
    key: 'purchase_receipts', group: '采购', title: '采购入库单', titleEn: 'Purchase Receipts',
    path: '/api/v1/procurement/receipts', permission: 'procurement.view_purchasereceipt',
    createPermission: isSimpleMode() ? 'procurement.add_purchasereceipt' : undefined,
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      ...(isSimpleMode() ? [text('supplier_name', '供应商'), text('warehouse_name', '仓库')]
        : [{ ...text('order_no', '采购订单号'), labelEn: 'Purchase order number' }]), date('receipt_date', '到货／入库日'), text('status', '状态')],
    fields: [select('supplier', '供应商', 'suppliers', true), select('warehouse', '入库仓库', 'warehouses', true),
      date('receipt_date', '入库日', true), text('remark', '备注')],
    lineCollections: [{ key: 'lines', label: '入库明细', labelEn: 'Receipt lines', fields: [
      ...productLine,
      { ...decimal('unit_price', '采购单价', true), labelEn: 'Purchase unit price', permission: 'procurement.view_purchase_amount' },
      { ...decimal('tax_rate', '税率 %'), labelEn: 'Tax %', numeric: { precision: 4, min: '0', max: '100' },
        permission: 'procurement.view_purchase_amount' },
      text('batch_no', '批次号', true), date('production_date', '生产日'), date('expiry_date', '到期日', true),
      ...(isSimpleMode() ? [
        { ...select('location', '实际货位', 'physical_locations'), labelEn: 'Actual location' },
        { ...select('pallet', '托盘', 'bin_pallets'), labelEn: 'Pallet' },
        { ...decimal('tare_weight_kg', '本行总皮重KG'), labelEn: 'Total tare KG',
          numeric: { precision: 3, min: '0' } },
        { ...decimal('occupied_volume_m3', '本行实际体积m³'), labelEn: 'Actual total volume m³',
          numeric: { precision: 6, min: '0.000001' } },
        { ...decimal('occupied_pallet_slots', '本行实际托盘位'), labelEn: 'Actual total pallet slots',
          numeric: { precision: 0, min: '0', max: '2147483647' } },
      ] : []),
    ] }],
  },
  receiving_packages: {
    key: 'receiving_packages', group: '采购', title: '收货暂存作业', titleEn: 'Receiving custody', navigation: false,
    path: '/api/v1/warehouse/receiving-packages', permission: 'warehouse_ops.view_receivingpackage', columns: [],
  },
  putaway_tasks: {
    key: 'putaway_tasks', group: '采购', title: '上架任务', titleEn: 'Putaway Tasks', navigation: false,
    path: '/api/v1/warehouse/putaway-tasks', permission: 'warehouse_ops.view_receivingpackage', columns: [],
  },
  sales_orders: {
    key: 'sales_orders', group: '销售', title: '销售订单', titleEn: 'Sales Orders',
    path: '/api/v1/sales/orders', permission: 'sales.view_salesorder',
    createPermission: 'sales.add_salesorder', editPermission: 'sales.change_salesorder',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      filterable(text('customer_name', '客户'), 'f_customer_name'),
      date('order_date', '下单日'), text('status', '状态'), { ...text('commercial_state', '商业放行'), labelEn:'Commercial release' }],
    fields: [select('customer', '客户', 'customers', true), select('warehouse', '默认仓库', 'warehouses'),
      { ...select('shipping_address', '收货地址', 'customer_addresses', true), permission: 'masterdata.view_customer_contact' },
      date('order_date', '下单日', true), date('delivery_date', '交期'),
      select('incoterm', '贸易术语', 'incoterms'), decimal('duty_rate', '关税率')],
    lineCollections: [{ key: 'lines', label: '订单明细', labelEn: 'Order lines', fields: [...productLine,
      { ...decimal('price', '售价'), permission: 'masterdata.view_product_amount' }, decimal('tax_rate', '税率')] }],
    actions: [...orderActions, { key: 'outbound', label: '销售出库', labelEn: 'Create outbound', statuses: ['approved', 'partial'],
      permission: 'sales.add_salesoutbound',
      fields: [select('warehouse_id', '出库仓', 'warehouses', true), date('outbound_date', '出库日', true)],
      lineFields: [decimal('order_line_id', '订单行ID', true), decimal('quantity', '出库数量', true)] }],
  },
  pricing_rules: {
    key: 'pricing_rules', group: '销售', title: '销售价格规则', titleEn: 'Sales price rules',
    path: '/api/v1/master-data/pricing-rules', permission: 'masterdata.view_pricingrule', columns: [],
  },
  sales_outbounds: {
    key: 'sales_outbounds', group: '销售', title: '销售出库单', titleEn: 'Sales Outbounds',
    path: '/api/v1/sales/outbounds', permission: 'sales.view_salesoutbound',
    createPermission: isSimpleMode() ? 'sales.add_salesoutbound' : undefined,
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      ...(isSimpleMode() ? [] : [{ ...text('related_doc_no', '销售订单'), labelEn: 'Sales order' }]),
      { ...text('customer_name', '客户'), labelEn: 'Customer' },
      { ...text('warehouse_name', '仓库'), labelEn: 'Warehouse' },
      date('outbound_date', '出库日'),
      text('status', '状态'), { ...decimal('cost_amount', '出库成本'), permission: 'masterdata.view_product_cost' }],
    fields: [select('customer', '客户', 'customers', true), select('warehouse', '出库仓库', 'warehouses', true),
      date('outbound_date', '出库日', true), text('remark', '备注')],
    lineCollections: [{ key: 'lines', label: '出库明细', labelEn: 'Outbound lines', fields: [
      ...productLine,
      { ...decimal('unit_price', '销售单价', true), labelEn: 'Sales unit price', permission: 'masterdata.view_product_amount' },
      { ...decimal('tax_rate', '税率 %'), permission: 'masterdata.view_product_amount' },
    ] }],
    actions: [{ key: 'submit', label: '提交审核', labelEn: 'Submit for approval', statuses: ['draft'], permission: 'sales.change_salesoutbound' },
      { key: 'post', label: '过账', labelEn: 'Post', statuses: ['approved'], permission: 'sales.change_salesoutbound' }],
  },
  deliveries: {
    key:'deliveries', group:'销售', title:'配送与签收', titleEn:'Delivery and receipt',
    path:'/api/v1/sales/deliveries', permission:'sales.view_deliverynote',
    columns:[{ ...text('doc_no','配送单号'), labelEn:'Delivery no.' },
      { ...text('outbound_no','出库单号'), labelEn:'Dispatch no.' },
      ...(isSimpleMode() ? [] : [{ ...text('order_no','销售订单'), labelEn:'Sales order' }]),
      { ...date('business_date','业务日期'), labelEn:'Business date' },text('status','状态')],
  },
  inventory_batches: {
    key: 'inventory_batches', group: '库存', title: '批次库存', titleEn: 'Inventory Batches',
    path: '/api/v1/inventory/batches', permission: 'inventory.view_inventorybatch',
    columns: [filterable(text('product_sku', 'SKU'), 'f_product_sku', true),
      filterable(text('batch_no', '批次'), 'f_batch_no'), text('warehouse_code', '仓库'),
      { ...text('zone_name', '库区'), labelEn: 'Zone' },
      filterable(text('location_code', '货位'), 'f_location_code'),
      filterable(text('pallet_code', '托盘'), 'f_pallet_code'),
      text('status', '库存状态'), date('expiry_date', '到期日'),
      { ...decimal('quantity_kg', '在账KG'), labelEn: 'On-hand KG' }, decimal('reserved_kg', '预占KG'),
      { ...decimal('available_kg', '可售KG'), labelEn: 'Saleable KG' },
      { ...decimal('cost_per_kg', isSimpleMode() ? '批次参考成本/KG' : '成本/KG'), permission: 'masterdata.view_product_cost' }],
  },
  inventory_movements: {
    key: 'inventory_movements', group: '库存', title: '库存流水', titleEn: 'Inventory Movements',
    path: '/api/v1/inventory/movements', permission: 'inventory.view_inventorymovement',
    columns: [date('occurred_on', '业务日'), filterable(text('batch_no', '批次'), 'f_batch_no'),
      text('movement_type', '类型'),
      decimal('quantity_kg', '变动KG'),
      { ...decimal('amount', '变动金额'), permission: 'masterdata.view_product_cost' },
      filterable(text('source_no', '来源单号'), 'f_source_no')],
  },
  stock_takes: {
    key: 'stock_takes', group: '库存', title: '库存盘点', titleEn: 'Stock Takes',
    path: '/api/v1/warehouse/stock-takes', permission: 'warehouse_ops.view_stocktake',
    createPermission: 'warehouse_ops.add_stocktake', editPermission: 'warehouse_ops.change_stocktake',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      text('warehouse_name', '仓库'), date('take_date', '盘点日'), text('status', '状态')],
    fields: [select('warehouse', '仓库', 'warehouses', true), date('take_date', '盘点日', true),
      text('take_type', '类型'), bool('freeze', '冻结出库', true)],
    lineCollections: [{ key: 'lines', label: '盲盘明细', labelEn: 'Blind-count lines', fields: [...(!isSimpleMode() ? [select('product', '商品', 'products', true), text('batch_no', '批次号'), date('expiry_date', '到期日')] : []),
      select('batch', '批次', 'inventory_batches', isSimpleMode()), decimal('actual_quantity_kg', '实盘KG', true)] }],
    actions: [...orderActions, { key: 'post', label: '盘点过账', labelEn: 'Post stock count', statuses: ['approved'],
      permission: 'warehouse_ops.change_stocktake' }],
  },
  transfers: {
    key: 'transfers', group: '库存', title: '库存调拨', titleEn: 'Transfers',
    path: '/api/v1/warehouse/transfers', permission: 'warehouse_ops.view_stocktransfer',
    createPermission: 'warehouse_ops.add_stocktransfer', editPermission: 'warehouse_ops.change_stocktransfer',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true), text('from_warehouse_name', '调出仓'),
      text('to_warehouse_name', '调入仓'), text('status', '状态')],
    fields: [select('from_warehouse', '调出仓', 'warehouses', true),
      select('to_warehouse', '调入仓', 'warehouses', true), date('transfer_date', '调拨日', true)],
    lineCollections: [{ key: 'lines', label: '调拨明细', labelEn: 'Transfer lines', fields: [select('source_batch', '源批次', 'inventory_batches', true),
      decimal('quantity_kg', '调拨KG', true)] }],
    actions: [...orderActions, { key: 'dispatch', label: '发运', labelEn: 'Dispatch', statuses: ['approved'],
      permission: 'warehouse_ops.change_stocktransfer' },
    { key: 'receive', label: '到货确认', labelEn: 'Confirm receipt', statuses: ['in_transit'], permission: 'warehouse_ops.change_stocktransfer' }],
  },
  scrap_orders: {
    key: 'scrap_orders', group: '库存', title: '报废单', titleEn: 'Scrap Orders',
    path: '/api/v1/warehouse/scrap-orders', permission: 'warehouse_ops.view_scraporder',
    createPermission: 'warehouse_ops.add_scraporder', editPermission: 'warehouse_ops.change_scraporder',
    columns: [filterable(text('doc_no', '单号'), 'f_doc_no', true),
      date('scrap_date', '报废日'), text('reason', '原因'),
      { ...decimal('loss_amount', '损失金额'), permission: 'masterdata.view_product_cost' }, text('status', '状态')],
    fields: [select('warehouse', '仓库', 'warehouses', true), date('scrap_date', '报废日', true),
      select('reason', '原因', 'scrap_reasons', true), text('evidence', '处置凭证', isSimpleMode())],
    lineCollections: [{ key: 'lines', label: '报废明细', labelEn: 'Scrap lines', fields: [select('batch', '批次', 'inventory_batches', true),
      decimal('quantity_kg', '报废KG', true)] }],
    actions: [...orderActions, { key: 'post', label: '报废过账', labelEn: 'Post scrap', statuses: ['approved'],
      permission: 'warehouse_ops.change_scraporder' }],
  },
  production_orders: {
    key: 'production_orders', group: '加工', title: '轻量加工', titleEn: 'Light processing',
    path: '/api/v1/processing/orders', permission: 'processing.view_processingorder',
    columns: [], fields: [], actions: [],
  },
  receivables: {
    key: 'receivables', group: '财务', title: '应收账款', titleEn: 'Receivables',
    path: '/api/v1/finance/receivables', permission: 'finance.view_receivable',
    columns: [filterable(text('invoice_no', '应收单号'), 'f_invoice_no', true),
      filterable(text('customer_name', '客户'), 'f_customer_name'), date('due_date', '到期日'),
      decimal('amount', '应收金额'), decimal('received_amount', '已收'), decimal('unreceived_amount', '未收')],
    // 收款请在「客户收款」登记（要先登记到账并上传凭证），旧的直接登记入口已停用。
  },
  receipts: {
    key: 'receipts', group: '财务', title: '收款单', titleEn: 'Receipts',
    path: '/api/v1/finance/receipts', permission: 'finance.view_receipt',
    columns: [filterable(text('receipt_no', '收款单号'), 'f_receipt_no', true),
      text('customer_name', '客户'), date('receipt_date', '收款日'),
      decimal('amount', '收款金额'), decimal('allocated_amount', '已核销'),
      decimal('held_amount', '订单预收占用'), decimal('available_amount', '未核销未占用')],
  },
  payables: {
    key: 'payables', group: '财务', title: '应付账款', titleEn: 'Payables',
    path: '/api/v1/finance/payables', permission: 'finance.view_payable',
    columns: [filterable(text('bill_no', '应付单号'), 'f_bill_no', true),
      filterable(text('supplier_name', '供应商'), 'f_supplier_name'), date('due_date', '到期日'),
      // gross_amount is 0 after reverse so 应付=已付+未付; amount stays historical face on detail.
      decimal('gross_amount', '应付金额'), decimal('paid_amount', '已付'), decimal('unpaid_amount', '未付'),
      text('status', '状态'), text('invoice_status', '发票状态')],
    actions: [{ key: 'match-invoice', label: '匹配发票', labelEn: 'Match invoice', permission: 'finance.change_payable',
      invoiceStatuses: ['estimated', 'disputed'],
      fields: [text('invoice_no', '发票号码', true), date('invoice_date', '发票日期', true),
        decimal('invoice_amount', '发票含税金额', true)] }],
  },
  payments: {
    key: 'payments', group: '财务', title: '付款单', titleEn: 'Payments',
    path: '/api/v1/finance/payments', permission: 'finance.view_payment',
    columns: [filterable(text('payment_no', '付款单号'), 'f_payment_no', true),
      filterable(text('supplier_name', '供应商'), 'f_supplier_name'), date('payment_date', '付款日'),
      decimal('amount', '付款金额'), decimal('allocated_amount', '已核销')],
  },
  responsibility_users: {
    key: 'responsibility_users', group: '系统', title: '员工责任部门', titleEn: 'Employee responsibility departments',
    path: '/api/v1/auth/responsibility-users', permission: 'accounts.view_user',
    columns: [text('username', '账号'), text('name_zh', '中文名'), text('name_en', '英文名'),
      text('department', '部门'), bool('is_active', '启用')],
    actions: [{ key: 'set-department', label: '配置部门', labelEn: 'Assign department',
      permission: 'accounts.change_user', fields: [text('department', '部门', true), text('reason', '配置原因', true)] }],
  },
  logs: {
    key: 'logs', group: '系统', title: '操作日志', titleEn: 'Operation Logs',
    path: '/api/v1/audit/logs', permission: 'audit.view_operationlog',
    columns: [text('created_at', '时间'), text('actor_name', '操作人'), text('action', '动作'),
      text('object_type', '对象'), filterable(text('object_no', '单号'), 'f_object_no')],
  },
}

export const optionsPath: Record<string, string> = {
  sales_orders: '/api/v1/sales/orders',
  revenue_sales_orders: '/api/v1/sales/orders',
  revenue_sales_outbounds: '/api/v1/sales/outbounds',
  putaway_sources: '/api/v1/warehouse/receiving-packages',
  customer_addresses: '/api/v1/master-data/customer-addresses',
  products: '/api/v1/master-data/products', customers: '/api/v1/master-data/customers',
  customer_assignees: '/api/v1/master-data/customers/assignees',
  supplier_assignees: '/api/v1/master-data/suppliers/assignees',
  warehouse_managers: '/api/v1/master-data/warehouses/manager-options',
  suppliers: '/api/v1/master-data/suppliers', warehouses: '/api/v1/master-data/warehouses',
  policy_warehouses: '/api/v1/master-data/warehouses', policy_products: '/api/v1/master-data/products',
  location_warehouses: '/api/v1/master-data/warehouses',
  locations: '/api/v1/master-data/locations', location_parents: '/api/v1/master-data/locations',
  physical_locations: '/api/v1/master-data/locations',
  pallets: '/api/v1/master-data/pallets',
  bin_pallets: '/api/v1/master-data/pallets',
  categories: '/api/v1/master-data/product-categories',
  category_parents: '/api/v1/master-data/product-categories',
  product_categories: '/api/v1/master-data/product-categories',
  // Trade terms remain a transaction reference, not a standalone record page.
  incoterms: '/api/v1/master-data/incoterms', units: '/api/v1/master-data/units',
  customer_levels: '/api/v1/master-data/customer-levels',
  purchase_requests: '/api/v1/procurement/requests',
  purchase_request_assignees: '/api/v1/procurement/requests/assignees',
  purchase_order_assignees: '/api/v1/procurement/orders/assignees',
  inventory_batches: '/api/v1/inventory/batches',
  receivables: '/api/v1/finance/receivables',
  payables: '/api/v1/finance/payables',
}

export interface ReportConfig {
  key: string
  title: string
  titleEn: string
  permission: string
  costPermission?: string
  amountPermission?: string
  path?: string
  supplierRequired?: boolean
  navigation?: boolean
  columns: readonly string[]
}

export const reports: ReportConfig[] = [
  { key: 'period-inventory', title: '期间进销存', titleEn: 'Period inventory', permission: 'inventory.view_inventorymovement', columns: [] },
  { key: 'profitability', title: '经营利润与税额', titleEn: 'Profit and tax', permission: 'sales.view_salesoutbound',
    costPermission: 'masterdata.view_product_cost', amountPermission: 'masterdata.view_product_amount', columns: [] },
  { key: 'stock-analysis', title: '动销与滞销分析', titleEn: 'Stock movement analysis', permission: 'inventory.view_inventorymovement', columns: [] },
  { key: 'location-map', title: '货位实况', titleEn: 'Physical location map', permission: 'inventory.view_inventorybatch', columns: [] },
  { key: 'average-cost', title: '库存平均成本', titleEn: 'Inventory average cost', permission: 'inventory.view_inventorybatch',
    costPermission: 'masterdata.view_product_cost',
    columns: ['warehouse', 'sku', 'product_name', 'quantity_kg', 'amount', 'average_cost', 'cost_status', 'source_count'] },
  { key: 'sales-daily', title: '每日销售出库汇总', titleEn: 'Daily sales dispatch', permission: 'sales.view_salesoutbound',
    columns: ['business_date', 'customer', 'warehouse', 'sku', 'quantity', 'unit', 'quantity_kg',
      'untaxed_amount', 'tax_amount', 'total_amount', 'outbound_count', 'source_documents'] },
  { key: 'inout', title: '进销存明细', titleEn: 'Inventory movement details', permission: 'inventory.view_inventorymovement',
    navigation: false,
    columns: ['sku', 'warehouse', 'type', 'quantity_kg', 'amount'] },
  { key: 'gross-margin', title: '毛利分析', titleEn: 'Gross margin analysis', permission: 'sales.view_salesoutbound',
    navigation: false,
    costPermission: 'masterdata.view_product_cost', amountPermission: 'masterdata.view_product_amount',
    columns: ['sku', 'revenue', 'cost', 'gross_profit'] },
  { key: 'receivable-aging', title: '应收账龄', titleEn: 'Receivable aging', permission: 'finance.view_receivable',
    columns: ['invoice_no', 'customer', 'due_date', 'outstanding', 'overdue_days', 'aging_status', 'source_path'] },
  { key: 'safety', title: '安全库存', titleEn: 'Safety stock', permission: 'inventory.view_inventorybatch',
    columns: ['sku', 'warehouse', 'available_kg', 'safety_kg', 'rule', 'source_path'] },
  { key: 'expiry', title: '临期预警', titleEn: 'Expiry alerts', permission: 'inventory.view_inventorybatch',
    columns: ['level', 'sku', 'warehouse', 'batch_no', 'stock_status', 'expiry_date', 'quantity_kg',
      'remaining_days', 'remaining_ratio', 'source_path'] },
  { key: 'scrap-loss', title: '报废损失', titleEn: 'Scrap loss', permission: 'inventory.view_inventorymovement',
    columns: ['sku', 'warehouse', 'loss_kg', 'loss_amount'] },
  { key: 'supplier-statement', title: '供应商对账单', titleEn: 'Supplier statement', permission: 'finance.view_payable',
    path: '/api/v1/finance/supplier-statements', supplierRequired: true,
    columns: ['bill_no', 'source_no', 'entry_type', 'bill_date', 'due_date', 'amount', 'paid_amount', 'outstanding', 'invoice_status'] },
]

